import { ReactNode } from "react";

/**
 * Staff Notation — bespoke design language, v2.
 *
 * v1 used a heavily-striped rose staff-paper background that competed with the
 * text for attention. v2 cleans it up: soft cream background, single rose
 * accent strip in the hero only, no repeating lines anywhere in the body.
 * Body type is the standard sans for readability; the serif is reserved for
 * titles and folio numbers.
 */

interface SNShellProps {
  title: string;
  description?: string;
  treatiseNumber?: string | number;
  subject?: string;
  children: ReactNode;
}

export function SNShell({ title, description, treatiseNumber, subject, children }: SNShellProps) {
  return (
    <div className="min-h-screen pb-16" style={{ background: "#fbf6ee", color: "#1f1408" }}>
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-rose-500/40 bg-gradient-to-b from-rose-100/50 via-rose-50/30 to-transparent">
        <div className="mx-auto max-w-3xl px-4 py-12 lg:px-8 lg:py-16">
          <div className="text-center">
            <div className="text-[11px] font-bold uppercase tracking-[0.4em] text-rose-700">
              Staff Notation · A Treatise
            </div>
            {treatiseNumber != null && (
              <div
                className="mt-3 text-6xl font-light text-rose-700/40"
                style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
              >
                {String(treatiseNumber).padStart(2, "0")}
              </div>
            )}
            <h1
              className="mt-3 text-3xl font-medium leading-tight text-stone-900 lg:text-5xl"
              style={{
                fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif',
                letterSpacing: "0.005em",
              }}
            >
              {title}
            </h1>
            {subject && (
              <div className="mt-3 italic text-stone-600">— {subject} —</div>
            )}
            {description && (
              <p className="mx-auto mt-5 max-w-2xl text-[16.5px] leading-[1.78] text-stone-700">
                {description}
              </p>
            )}
            <div className="mx-auto mt-6 h-px w-32 bg-rose-400/60" />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-3xl space-y-6 px-4 py-10 lg:px-8 lg:py-12">{children}</main>
    </div>
  );
}

/* Folio — a section of the treatise. */
export function Folio({
  number,
  title,
  children,
}: {
  number?: string | number;
  title?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-rose-200/70 bg-white p-7 shadow-[0_2px_20px_rgba(244,63,94,0.05)]">
      {(number != null || title) && (
        <div className="mb-4 flex items-baseline gap-3 border-b border-rose-200/60 pb-3">
          {number != null && (
            <div
              className="text-2xl font-medium text-rose-700/80"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              §{number}
            </div>
          )}
          {title && (
            <h2
              className="text-2xl font-semibold text-stone-900 lg:text-3xl"
              style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
            >
              {title}
            </h2>
          )}
        </div>
      )}
      <div className="space-y-3.5 text-[16px] leading-[1.78] text-stone-800">
        {children}
      </div>
    </section>
  );
}

/* Engraving — handwritten-note frame */
export function Engraving({
  src,
  caption,
}: {
  src: string;
  caption?: string;
}) {
  return (
    <figure className="my-6">
      <div className="overflow-hidden rounded-lg border-2 border-rose-300/70 bg-white p-1 shadow-[0_4px_20px_rgba(244,63,94,0.08)]">
        <img
          src={src}
          alt={caption ?? "Engraving from the treatise"}
          className="block max-w-xl mx-auto w-auto"
          loading="lazy"
        />
      </div>
      {caption && (
        <figcaption className="mt-2 text-center text-[13px] italic text-stone-600">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* Glossarium — vocabulary card. */
export function Glossarium({ children }: { children: ReactNode }) {
  return (
    <aside className="my-5 rounded-lg border-l-4 border-rose-400 bg-rose-50/70 px-5 py-4">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.32em] text-rose-700">
        ✦ Glossarium
      </div>
      <div className="space-y-2 text-[15.5px] leading-relaxed text-stone-800">
        {children}
      </div>
    </aside>
  );
}

/* Marginalia — italic side-note. */
export function Marginalia({ children }: { children: ReactNode }) {
  return (
    <aside className="my-3 rounded-md border-l-2 border-rose-400 bg-rose-50/40 py-2 pl-4 pr-2 text-[14.5px] italic text-stone-700">
      {children}
    </aside>
  );
}

/** A live VexFlow staff with playable notes — wraps the existing Notation component
 *  with a proper inline frame for the SN treatise visual style. */
export function StaffExample({
  caption,
  children,
}: {
  caption?: string;
  children: ReactNode;
}) {
  return (
    <figure className="my-5 overflow-x-auto rounded-lg border border-rose-200/70 bg-white p-4 shadow-[0_2px_15px_rgba(244,63,94,0.04)]">
      <div className="notation-paper">{children}</div>
      {caption && (
        <figcaption className="mt-3 text-center text-[13px] italic text-stone-600">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
